import IconButton from '@mui/material/IconButton'
import {
  OptionsObject,
  ProviderContext,
  SnackbarKey,
  useSnackbar,
} from 'notistack'
import React, { useEffect } from 'react'

import CloseIcon from 'components/Icons/DialogClose'

const variants = ['default', 'error', 'success', 'warning', 'info'] as const

export type ToastType = {
  error: CreateToast
  message: CreateToast
  showDOMToasts: (query: string) => void
  success: CreateToast
  warning: CreateToast
}

type CreateToast = (text: string, options: Options) => void

type Options = OptionsObject & {
  analyticsId?: string
  'data-test-id'?: 'notification'
  'data-variant'?: Variant
  onClose?: () => void
  textAsHtml?: boolean
  variant?: Variant
}

type Variant = (typeof variants)[number]

let instance: ProviderContext | null
const queue: Array<[React.JSX.Element | string, Options]> = []

// HACK: Proxy component to expose notistack API for legacy toasts and backward compatibility
export function SnackbarGlobalProxy() {
  const snackbar = useSnackbar()

  useEffect(() => {
    mount(snackbar)

    if (window.flash?.error) {
      error(window.flash.error, { textAsHtml: true })
    }

    if (window.flash?.success) {
      success(window.flash.success, { textAsHtml: true })
    }

    return unmount
  })

  return null
}

function createToast(...args: [React.JSX.Element | string, Options]) {
  if (instance) {
    showSnackbar(...args)
  } else {
    queue.push(args)
  }
}

function error(text: React.JSX.Element | string, options: Options = {}) {
  return createToast(text, {
    variant: 'error',
    ...options,
  })
}

function message(text: React.JSX.Element | string, options: Options = {}) {
  return createToast(text, {
    variant: 'info',
    ...options,
  })
}

function mount(snackbar: ProviderContext) {
  instance = snackbar
  while (queue.length) createToast(...(queue.shift() as [string, Options]))
}

function showDOMToasts(query: string) {
  Array.from(document.querySelectorAll<HTMLElement>(query))
    .filter((element) => {
      if (
        window.flash?.error &&
        element.dataset.type === 'error' &&
        window.flash.error === element.dataset.text
      ) {
        return false
      }

      if (
        window.flash?.success &&
        element.dataset.type === 'success' &&
        window.flash.success === element.dataset.text
      ) {
        return false
      }

      return true
    })
    .forEach((element) => {
      const { text, type, ...options } = element.dataset

      if (text) {
        createToast(text, {
          textAsHtml: true,
          variant:
            type && (variants as ReadonlyArray<string>).includes(type)
              ? (type as Variant)
              : 'info',
          ...options,
        })
      }
    })
}

function showSnackbar(text: React.JSX.Element | string, options: Options = {}) {
  const action = (key: SnackbarKey) => (
    <IconButton
      color="inherit"
      data-js-hook-analytics={options.analyticsId}
      data-test-id="close-button"
      onClick={() => {
        options.onClose?.()
        instance?.closeSnackbar(key)
      }}
      size="large"
    >
      <CloseIcon />
    </IconButton>
  )

  const { textAsHtml, ...rest } = options

  const content = textAsHtml ? (
    <div dangerouslySetInnerHTML={{ __html: text }} />
  ) : (
    text
  )

  instance?.enqueueSnackbar(content, {
    ...rest,
    action,
    SnackbarProps: {
      'data-test-id': 'notification',
      'data-variant': rest.variant, // Expose attribute for QA tests
      style: { top: 36 },
    },
  })
}

function success(text: React.JSX.Element | string, options: Options = {}) {
  return createToast(text, {
    variant: 'success',
    ...options,
  })
}

function unmount() {
  instance = null
}

function warning(text: React.JSX.Element | string, options: Options = {}) {
  return createToast(text, {
    variant: 'warning',
    ...options,
  })
}

const Toast = {
  error,
  message,
  showDOMToasts,
  success,
  warning,
}

export default Toast
